import type { ResearchesByDate } from '@/components/WellLoggingOperations/types';
import type {
    ExtendedResearch,
    ExtendedResearchesByType,
    ResearchNode,
    Researches,
    DateCurveKey,
    IntervalCurveKey,
    Schema,
    ResearchesByType,
    Research,
} from '@/components/WellLoggingOperations/types/researches';
import { cloneDeep } from 'lodash';

/**
 * Проверяет, является ли узел родительским
 * @param node Узел исследования
 * @returns true, если узел является родительским, иначе false
 */
const isParentNode = (node: ResearchNode): node is ResearchesByDate | ExtendedResearchesByType => {
    return node.children !== undefined;
};

/**
 * Создает функцию фильтрации по заголовку
 * @param keyword Ключевое слово для поиска
 * @returns Функция фильтрации
 */
const filterByTitleCb =
    (keyword: string) =>
    (item: Researches): boolean =>
        item.title?.toLowerCase().includes(keyword.toLowerCase());

/**
 * Создает функцию фильтрации по ключу
 * @param keys Массив ключей для фильтрации
 * @returns Функция фильтрации
 */
const filterByKeyCb =
    (keys: string[]) =>
    (item: Researches): boolean =>
        keys.includes(item.key);

/**
 * Фильтрует исследования по заданному критерию
 * @param array Массив исследований
 * @param filterCb Функция фильтрации
 * @returns Отфильтрованный массив исследований
 */
const filterResearches = (
    array: ResearchesByDate[],
    filterCb: (item: ExtendedResearch) => boolean
): ResearchesByDate[] => {
    const filterNodes = (items: Researches[] | undefined): Researches[] => {
        if (!Array.isArray(items) || items.length === 0) {
            return [];
        }
        return items
            .map(item => {
                if ('children' in item && item.children) {
                    const filteredChildren = filterNodes(item.children as Researches[]);
                    if (filteredChildren.length > 0) {
                        return { ...item, children: filteredChildren };
                    }
                } else if (filterCb(item as ExtendedResearch)) {
                    return item;
                }
                return null;
            })
            .filter((item): item is Researches => item !== null);
    };

    return (array || [])
        .map(({ title, key, children }) => ({
            title,
            key,
            children: filterNodes(children as Researches[]) as ExtendedResearchesByType[],
        }))
        .filter(({ children }) => children.length > 0);
};

/**
 * Получает все ключи из дерева исследований
 * @param array Массив исследований
 * @returns Массив ключей
 */
const getKeysFromTree = (array: Researches[]): string[] => {
    const result: string[] = [];
    const items = [...array];

    while (items.length) {
        const item = items.pop();
        if (item) {
            result.push(item.key);

            if ('children' in item && item.children) {
                items.push(...(item.children as Researches[]));
            }
        }
    }

    return result;
};

const mapResearchesByKey = (researchesByDate: ResearchesByDate[]): Record<string, Research> => {
    const researchMap: Record<string, Research> = {};
  
    researchesByDate.forEach(({ children: researchesByType }) => {
      researchesByType.forEach(({ children: researches }) => {
        researches.forEach((research) => {
          researchMap[research.key] = research;
        });
      });
    });
  
    return researchMap;
  };

/**
 * Проверяет, выбраны ли все дочерние элементы
 * @param selectedKeys Массив выбранных ключей
 * @param parents Массив родительских элементов
 * @param selected Флаг выбора
 * @returns Обновленный массив выбранных ключей
 */
const checkAllChildrenSelected = (selectedKeys: string[], parents: ResearchNode[], selected: boolean): string[] => {
    for (const parent of parents) {
        let allChildrenSelected = true;
        if (isParentNode(parent)) {
            if (parent.children)
                for (const child of parent.children) {
                    if (!selectedKeys.includes(child.key)) {
                        allChildrenSelected = false;
                        break;
                    }
                }

            if (selected && allChildrenSelected && !selectedKeys.includes(parent.key)) {
                selectedKeys.push(parent.key);
            }

            if (!selected && !allChildrenSelected && selectedKeys.includes(parent.key)) {
                selectedKeys = selectedKeys.filter(key => key !== parent.key);
            }
        }
    }

    return selectedKeys;
};

/**
 * Получает все ключи дочерних элементов
 * @param node Узел исследования
 * @returns Массив ключей
 */
const getAllChildKeys = (node: ResearchNode): string[] => {
    let keys: string[] = node ? [node.key] : [];
    if (node?.children) {
        node.children.forEach(child => {
            keys = [...keys, ...getAllChildKeys(child as ResearchNode)];
        });
    }
    return keys;
};

/**
 * Находит все родительские элементы по ключу дочернего элемента
 * @param nodes Массив узлов
 * @param childKey Ключ дочернего элемента
 * @param path Путь к элементу
 * @returns Массив родительских элементов
 */
const findAllParentsByChildKey = (
    nodes: ResearchNode[],
    childKey: string,
    path: ResearchNode[] = []
): ResearchNode[] | null => {
    for (const node of nodes) {
        if (node.key === childKey) {
            return path.reverse();
        }

        if (node.children) {
            const result = findAllParentsByChildKey(node.children as ResearchNode[], childKey, [...path, node]);
            if (result) return result;
        }
    }
    return null;
};

/**
 * Получает узел по ключу
 * @param nodes Массив узлов
 * @param searchKey Ключ для поиска
 * @returns Найденный узел или null
 */
const getNodeByKey = (nodes: ResearchNode[], searchKey: string): ResearchNode | null => {
    for (const node of nodes) {
        if (node.key === searchKey) {
            return node;
        }

        if (node.children) {
            const foundNode = getNodeByKey(node.children as ResearchNode[], searchKey);
            if (foundNode) {
                return foundNode;
            }
        }
    }

    return null;
};

/**
 * Обновляет выбранные элементы в данных исследований на основе фильтров
 * @param data Массив данных исследований
 * @param researchCode Код исследования для фильтрации
 * @param dateCurveKey Ключ для фильтрации по дате кривой
 * @param intervalCurveKey Ключ для фильтрации по интервалу кривой
 * @returns Обновленный массив данных исследований
 */
const updateDataSelection = (
    data: ResearchesByDate[],
    researchCode: string | null | undefined,
    dateCurveKey: DateCurveKey,
    intervalCurveKey: IntervalCurveKey
): ResearchesByDate[] => {
    if (!data || !Array.isArray(data)) return [];

    // Проверяем, являются ли данные интервальными
    const isInterval = data.some(item => item?.children?.some(childItem => !childItem.children));

    // Создаем множество для отслеживания уже использованных ключей
    const usedKeys = new Set<string>();

    // Функция для обеспечения уникальности ключа
    const ensureUniqueKey = (key: string, prefix: string, index: number): string => {
        if (!key || usedKeys.has(key)) {
            const newKey = `${prefix}-${index}-${Date.now()}`;
            usedKeys.add(newKey);
            return newKey;
        }
        usedKeys.add(key);
        return key;
    };

    // Обновляем данные в зависимости от типа (интервальные или нет)
    return data.map((res1, index1) => {
        const res1Key = ensureUniqueKey(res1.key, 'date-research', index1);

        if (isInterval) {
            // Для интервальных данных обновляем на уровне ResearchesByType
            return {
                ...res1,
                key: res1Key,
                children: res1.children.map((res2, index2) => {
                    const extendedRes2 = res2 as ExtendedResearchesByType;
                    const res2Key = ensureUniqueKey(extendedRes2.key, `${res1Key}-type`, index2);

                    return {
                        ...extendedRes2,
                        key: res2Key,
                        isSelected: researchCode
                            ? extendedRes2.research_code === researchCode && Boolean(extendedRes2[intervalCurveKey])
                            : Boolean(extendedRes2[dateCurveKey]),
                    };
                }),
            };
        } else {
            // Для обычных данных обновляем на уровне Research
            return {
                ...res1,
                key: res1Key,
                children: res1.children.map((res2, index2) => {
                    const res2Key = ensureUniqueKey(res2.key, `${res1Key}-type`, index2);

                    return {
                        ...res2,
                        key: res2Key,
                        children: res2.children.map((res3: ExtendedResearch, index3) => {
                            const res3Key = ensureUniqueKey(res3.key, `research-${index2}-${index3}`, index3);
                            
                            return {
                                ...res3,
                                key: res3Key,
                                isSelected: researchCode
                                    ? res3.research_code === researchCode &&
                                      Boolean(res3[intervalCurveKey])
                                    : Boolean(res3[dateCurveKey]),
                            };
                        }),
                    };
                }),
            };
        }
    });
};

/**
 * Проверка наличия вложенных детей
 * @param data Массив данных для проверки
 * @returns true, если есть вложенные дети
 */
const checkNestedChildren = (data: ResearchesByDate[]): boolean => {
    if (!data || !Array.isArray(data) || data.length === 0) return false;

    // Проверяем структуру данных на каждом уровне
    return data.some(item => {
        if (!item || !item.children || !Array.isArray(item.children)) return false;

        return item.children.some(childItem => {
            // Проверяем, есть ли у дочернего элемента свойство children
            if (!('children' in childItem)) return true;

            // Если children существует, проверяем, является ли оно пустым массивом
            const children = childItem.children;
            return !children || !Array.isArray(children) || children.length === 0;
        });
    });
};

const mergeResearches = (existingResearches: Research[], newResearches: Research[]): Research[] => {
    const mergedMap = new Map(existingResearches.map(res => [res.key, cloneDeep(res)]));
    
    for (const newResearch of newResearches) {
        if (mergedMap.has(newResearch.key)) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const existing = mergedMap.get(newResearch.key)!;
            existing.children = mergeResearches(existing.children ?? [], newResearch.children ?? []);
        } else {
            mergedMap.set(newResearch.key, cloneDeep(newResearch));
        }
    }
    
    return Array.from(mergedMap.values());
};

const mergeResearchesByType = (existingTypes: ResearchesByType[], newTypes: ResearchesByType[]): ResearchesByType[] => {
    const mergedMap = new Map(existingTypes.map(type => [type.key, cloneDeep(type)]));
    
    for (const newType of newTypes) {
        if (mergedMap.has(newType.key)) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const existing = mergedMap.get(newType.key)!;
            existing.children = mergeResearches(existing.children, newType.children);
        } else {
            mergedMap.set(newType.key, cloneDeep(newType));
        }
    }
    
    return Array.from(mergedMap.values());
};

const mergeSchemas = (schemas: Schema[]): Schema => {
    const mergedSchema: Schema = {};
    
    for (const schema of schemas) {
        for (const [key, researchesByDate] of Object.entries(schema)) {
            if (!mergedSchema[key]) {
                mergedSchema[key] = cloneDeep(researchesByDate);
            } else {
                const mergedDatesMap = new Map(
                    mergedSchema[key].map(date => [date.key, cloneDeep(date)])
                );
                
                for (const newDate of researchesByDate) {
                    if (mergedDatesMap.has(newDate.key)) {
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        const existingDate = mergedDatesMap.get(newDate.key)!;
                        existingDate.children = mergeResearchesByType(existingDate.children, newDate.children);
                    } else {
                        mergedDatesMap.set(newDate.key, cloneDeep(newDate));
                    }
                }
                
                mergedSchema[key] = Array.from(mergedDatesMap.values());
            }
        }
    }
    
    return mergedSchema;
};

export {
    mapResearchesByKey,
    mergeSchemas,
    filterByKeyCb,
    filterByTitleCb,
    filterResearches,
    findAllParentsByChildKey,
    getKeysFromTree,
    getAllChildKeys,
    checkAllChildrenSelected,
    getNodeByKey,
    updateDataSelection,
    checkNestedChildren,
};
